import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { SF_UI } from './ui/tokens';

type PageHeroProps = {
  /** Small uppercase eyebrow above the title (e.g. Legacy · About) */
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Optional icon or badge above the title */
  icon?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** Tighter vertical padding for compact intros */
  compact?: boolean;
};

/**
 * Immersive slate/primary page intro matching the One World hub hero.
 * Use on public marketing/content pages for same-site cohesion.
 */
const PageHero = ({
  eyebrow,
  title,
  subtitle,
  icon,
  children,
  className = '',
  compact = false,
}: PageHeroProps) => {
  return (
    <section
      className={`${SF_UI.systemId} ${SF_UI.heroShell} ${compact ? 'pb-12' : ''} ${className}`}
      data-sf-ui="page-hero"
    >
      <div className={SF_UI.heroOrbPrimary} aria-hidden />
      <div className={SF_UI.heroOrbSecondary} aria-hidden />
      <div className={SF_UI.heroOrbSoft} aria-hidden />

      <div className="container relative z-10 mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          className="mx-auto max-w-3xl text-center"
        >
          {eyebrow && <p className={`mb-3 ${SF_UI.microLabel}`}>{eyebrow}</p>}

          {icon && (
            <div className="mb-5 flex items-center justify-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/15 bg-white/10 shadow-lg shadow-primary/20 backdrop-blur">
                {icon}
              </span>
            </div>
          )}

          <h1 className={`mb-4 ${SF_UI.heroTitle}`}>{title}</h1>

          {subtitle && (
            <div className={`mb-2 ${SF_UI.heroSubtitle}`}>{subtitle}</div>
          )}

          {children && <div className="mt-6">{children}</div>}
        </motion.div>
      </div>
    </section>
  );
};

export default PageHero;
